/**
 * Decides which toolbar commands the row can still afford.
 *
 * Priority decides *which* commands drop. Document order decides where the
 * survivors sit, which is the caller's job; this module only returns id sets.
 *
 * Items may declare `movable: false`, which pins them to the row no matter how
 * tight it gets. Quill's `.ql-picker` dropdowns use that: relocating one breaks
 * the dropdown it owns, so they are never candidates and the arithmetic simply
 * treats their width as fixed overhead.
 */

const sum = (ids, widths) => ids.reduce((total, id) => total + (widths[id] || 0), 0);

/**
 * @param {object} opts
 * @param {Array<{id: string, priority: number, movable?: boolean}>} opts.items - document order
 * @param {object} opts.widths       - measured width per item id
 * @param {number} opts.available    - room the row has to work with
 * @param {number} opts.triggerWidth - width of the ... button, charged once it shows
 * @returns {{visibleIds: string[], overflowIds: string[], hasOverflow: boolean}}
 */
export function computeOverflow({ items, widths, available, triggerWidth = 0 }) {
  const allIds = items.map((item) => item.id);
  const everything = { visibleIds: allIds, overflowIds: [], hasOverflow: false };

  // Before the measure pass some widths are unknown. Showing the whole row is
  // the honest answer - and it is the state the measure pass needs anyway.
  if (!items.length || !Number.isFinite(available) || allIds.some((id) => widths[id] == null)) {
    return everything;
  }

  if (sum(allIds, widths) <= available) return everything;

  // Something has to go, so the trigger is now on screen and costs its width.
  const budget = available - triggerWidth;
  const dropped = new Set();

  // Stable ordering: equal priorities fall in document order, so the drop
  // sequence is deterministic rather than engine-dependent.
  const candidates = items
    .map((item, index) => ({ ...item, index }))
    .filter((item) => item.movable !== false)
    .sort((a, b) => a.priority - b.priority || a.index - b.index);

  for (const item of candidates) {
    const remaining = allIds.filter((id) => !dropped.has(id));
    if (sum(remaining, widths) <= budget) break;
    dropped.add(item.id);
  }

  return {
    visibleIds: allIds.filter((id) => !dropped.has(id)),
    overflowIds: allIds.filter((id) => dropped.has(id)),
    hasOverflow: dropped.size > 0,
  };
}
